import React, { ChangeEvent, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";

import profile from "../../assets/images/profile.png";
import {
  ActivityButton,
  AppRichEditor,
  TimeInput,
} from "../../components/Common";
import {
  State,
  SkillDto,
  CreateOlympicQuestionRequest,
  OlympicQuestionDto,
} from "../../interfaces";
import { QuestionOptionEnum } from "../../enums";
import {
  addOlympicQuestion,
  getOlympicQuestion,
  updateOlympicQuestion,
  uploadOlympicQuestionDisplayImage,
} from "../../store/actions";
import { MediaDto } from "../../interfaces/media";
import { IMAGE_ACCEPT } from "../../constants";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  olympicId: string;
};
export const AddEditOlympicQuestionsModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  olympicId,
}) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  //#endregion

  //#region internal state
  const [request] = useState<CreateOlympicQuestionRequest>();
  const [skillsSuggestions, setSkillsSuggestions] = useState<SkillDto[]>([]);
  //#endregion

  //#region redux state
  const {
    loading: addingQuestion,
    success: addQuestionSuccess,
    error: addQuestionError,
  } = useSelector(
    (state: any): State<OlympicQuestionDto> => state.olympic.addOlympicQuestion
  );

  const {
    loading: updatingQuestion,
    success: updateQuestionSuccess,
    error: updateQuestionError,
  } = useSelector(
    (state: any): State<OlympicQuestionDto> =>
      state.olympic.updateOlympicQuestion
  );

  const {
    error: getQuestionError,
    response: question,
    loading: gettingQuestion,
    success: getQuestionSuccess,
  } = useSelector(
    (state: any): State<OlympicQuestionDto> => state.olympic.olympicQuestion
  );

  const {
    error: displayImageUploadingError,
    loading: uploadingDisplayImage,
    success: uploadDisplayImageSuccess,
    response: uploadedDisplayImage,
  } = useSelector(
    (state: any): State<MediaDto> => state.olympic.olympicQuestionDisplayImage
  );

  const {
    error: getAISkillsSuggestionError,
    loading: gettingAISKillsSuggestion,
    success: getAISkillsSuggestionSuccess,
    response: aiSuggestedSkills,
  } = useSelector(
    (state: any): State<SkillDto[]> => state.skill.skillsSuggestion
  );
  //#endregion

  //#region form
  const form = useFormik<CreateOlympicQuestionRequest>({
    enableReinitialize: true,
    initialValues: {
      content: {
        en: request?.content.en || "",
        ar: request?.content.ar || "",
      },
      timeDuration: request?.timeDuration || "3:00",
      marks: request?.marks || 1,
      displayImageId: request?.displayImageId,
      olympicId: request?.olympicId || olympicId,
      optionA: {
        en: request?.optionA.en || "",
        ar: request?.optionA.ar || "",
      },
      optionB: {
        en: request?.optionB.en || "",
        ar: request?.optionB.ar || "",
      },
      optionC: {
        en: request?.optionC.en || "",
        ar: request?.optionC.ar || "",
      },
      optionD: {
        en: request?.optionD.en || "",
        ar: request?.optionD.ar || "",
      },
      correctOption: request?.correctOption || QuestionOptionEnum.OPTION_A,
    },
    initialErrors: addQuestionError || updateQuestionError,
    validationSchema: Yup.object({
      content: Yup.object({
        en: Yup.string(),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          })
        ),
      }),
      timeDuration: Yup.string().required(
        t("isNotEmpty", {
          displayName: "timeDuration",
        })
      ),
      marks: Yup.string().required(
        t("isNotEmpty", {
          displayName: "marks",
        })
      ),
      olympicId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "olympic",
        })
      ),
      optionA: Yup.object({
        en: Yup.string(),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          })
        ),
      }),
      optionB: Yup.object({
        en: Yup.string(),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          })
        ),
      }),
      optionC: Yup.object({
        en: Yup.string().nullable(),
        ar: Yup.string().nullable(),
      }),
      optionD: Yup.object({
        en: Yup.string().nullable(),
        ar: Yup.string().nullable(),
      }),
      correctOption: Yup.string().required(
        t("isNotEmpty", {
          displayName: "correctOption",
        })
      ),
    }),

    onSubmit: (values) => {
      if (id) {
        dispatch(
          updateOlympicQuestion(
            id,
            {
              ...values,
              content: {
                ...values.content,
                en: values.content.ar,
              },
              optionA: {
                ...values.optionA,
                en: values.optionA.ar,
              },
              optionB: {
                ...values.optionB,
                en: values.optionB.ar,
              },
              optionC: {
                ...values.optionC,
                en: values.optionC.ar,
              },
              optionD: {
                ...values.optionD,
                en: values.optionD.ar,
              },
              olympicId: olympicId,
            },
            history
          )
        );
      } else {
        dispatch(
          addOlympicQuestion(
            {
              ...values,
              content: {
                ...values.content,
                en: values.content.ar,
              },
              optionA: {
                ...values.optionA,
                en: values.optionA.ar,
              },
              optionB: {
                ...values.optionB,
                en: values.optionB.ar,
              },
              optionC: {
                ...values.optionC,
                en: values.optionC.ar,
              },
              optionD: {
                ...values.optionD,
                en: values.optionD.ar,
              },
              olympicId: olympicId,
            },
            history
          )
        );
      }
    },
  });
  //#endregion

  //#region effects

  useEffect(() => {
    if (id) {
      dispatch(getOlympicQuestion(id, history));
    }
  }, [id]);

  useEffect(() => {
    if (id && question) {
      form.setValues({
        content: question.content,
        olympicId: question.olympic.id,
        correctOption: question.correctOption,
        marks: Number(question.marks),
        optionA: question.optionA,
        optionB: question.optionB,
        optionC: question.optionC,
        optionD: question.optionD,
        timeDuration: question.timeDuration,
        displayImageId: question.displayImage?.id,
      });
    }
  }, [question]);

  useEffect(() => {
    if (
      (!addingQuestion && addQuestionSuccess) ||
      (!updatingQuestion && updateQuestionSuccess)
    ) {
      onClose();
    }
  }, [addingQuestion, updatingQuestion]);

  useEffect(() => {
    if (!gettingAISKillsSuggestion && getAISkillsSuggestionSuccess) {
      setSkillsSuggestions([
        ...(aiSuggestedSkills || []),
        ...skillsSuggestions,
      ]);
    }
  }, [gettingAISKillsSuggestion]);

  useEffect(() => {
    if (
      !uploadingDisplayImage &&
      uploadDisplayImageSuccess &&
      uploadedDisplayImage
    ) {
      form.setFieldValue("displayImageId", uploadedDisplayImage.id);
    }
  }, [uploadingDisplayImage]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    setSkillsSuggestions([]);
    onCloseClick(!show);
  };

  const onChangeQuestionImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      dispatch(
        uploadOlympicQuestionDisplayImage(imageFile, history, question?.id)
      );
    }
  };

  const countUpMarks = () => {
    form.setFieldValue("marks", form.values.marks + 1);
  };

  const countDownMarks = () => {
    if (form.values.marks > 1)
      form.setFieldValue("marks", form.values.marks + -1);
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="modal-title"
            toggle={() => {
              onClose();
            }}
          >
            {t("addItem", { item: "question" })}
          </ModalHeader>
          <div className="mt-3">
            {typeof addQuestionError === "string" && (
              <Alert color="danger" className="rounded-0 mb-0">
                <p className="mb-0">{addQuestionError}</p>
              </Alert>
            )}
            {typeof updateQuestionError === "string" && (
              <Alert color="danger" className="rounded-0 mb-0">
                <p className="mb-0">{updateQuestionError}</p>
              </Alert>
            )}
            {typeof getQuestionError === "string" && (
              <Alert color="danger" className="rounded-0 mb-0">
                <p className="mb-0">{getQuestionError}</p>
              </Alert>
            )}
            {typeof getAISkillsSuggestionError === "string" && (
              <Alert color="danger" className="rounded-0 mb-0">
                <p className="mb-0">{getAISkillsSuggestionError}</p>
              </Alert>
            )}
            {typeof displayImageUploadingError === "string" && (
              <Alert color="danger" className="rounded-0 mb-0">
                <p className="mb-0">{displayImageUploadingError}</p>
              </Alert>
            )}
          </div>
          <ModalBody>
            <Row>
              <Col sm={12}>
                {/* Question Image */}
                <div className="col-auto text-center">
                  <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                    <img
                      src={
                        uploadedDisplayImage?.path ||
                        question?.displayImage?.path ||
                        profile
                      }
                      className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                      alt="user-profile"
                    />
                    <div className="avatar-xl p-0 rounded-circle profile-photo-edit">
                      <Input
                        id="question_display_image"
                        type="file"
                        className="profile-img-file-input"
                        accept={IMAGE_ACCEPT}
                        onChange={onChangeQuestionImage}
                        disabled={uploadingDisplayImage}
                      />
                      <Label
                        htmlFor="question_display_image"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body">
                          {uploadingDisplayImage && <Spinner />}
                          {!uploadingDisplayImage && (
                            <i className="ri-camera-fill"></i>
                          )}
                        </span>
                      </Label>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xl="12">
                <div className="mb-3">
                  <Label htmlFor="content.ar" className="form-label">
                    {t("questionContentAr")}
                    <span className="text-danger">*</span>
                  </Label>
                  <AppRichEditor
                    initialValue={question?.content?.ar || ""}
                    onChange={(data: any) => {
                      form.setFieldValue("content.ar", data);
                    }}
                    onKeyUp={(data: any) => {
                      if (data.trim().length == 0) {
                        form.setFieldError(
                          "content.ar",
                          t("isNotEmpty", {
                            displayName: "arabicValue",
                          })
                        );
                      } else {
                        form.setFieldError("content.ar", "");
                      }
                    }}
                    onFocus={() => {
                      form.setFieldTouched("content.ar", true, true);
                    }}
                  />
                  {form.touched.content?.ar && form.errors.content?.ar ? (
                    <Label className="invalid-feedback d-block">
                      {form.errors.content?.ar as string}
                    </Label>
                  ) : null}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xl="6">
                <Label htmlFor="optionA.ar" className="form-label">
                  {t("answerA")}
                  <span className="text-danger">*</span>
                </Label>
                <div className="mb-3">
                  <AppRichEditor
                    initialValue={question?.optionA?.ar || ""}
                    onChange={(data: any) => {
                      form.setFieldValue("optionA.ar", data);
                    }}
                    onKeyUp={(data: any) => {
                      if (data.trim().length == 0) {
                        form.setFieldError(
                          "optionA.ar",
                          t("isNotEmpty", {
                            displayName: "arabicValue",
                          })
                        );
                      } else {
                        form.setFieldError("optionA.ar", "");
                      }
                    }}
                    onFocus={() => {
                      form.setFieldTouched("optionA.ar", true, true);
                    }}
                  />
                  {form.touched.optionA?.ar && form.errors.optionA?.ar ? (
                    <Label className="invalid-feedback d-block">
                      {form.errors.optionA?.ar as string}
                    </Label>
                  ) : null}
                </div>
              </Col>
              <Col xl="6">
                <Label htmlFor="optionB.ar" className="form-label">
                  {t("answerB")}
                  <span className="text-danger">*</span>
                </Label>
                <div className="mb-3">
                  <AppRichEditor
                    initialValue={question?.optionB?.ar || ""}
                    onChange={(data: any) => {
                      form.setFieldValue("optionB.ar", data);
                    }}
                    onKeyUp={(data: any) => {
                      if (data.trim().length == 0) {
                        form.setFieldError(
                          "optionB.ar",
                          t("isNotEmpty", {
                            displayName: "arabicValue",
                          })
                        );
                      } else {
                        form.setFieldError("optionB.ar", "");
                      }
                    }}
                    onFocus={() => {
                      form.setFieldTouched("optionB.ar", true, true);
                    }}
                  />
                  {form.touched.optionB?.ar && form.errors.optionB?.ar ? (
                    <Label className="invalid-feedback d-block">
                      {form.errors.optionB?.ar as string}
                    </Label>
                  ) : null}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xl="6">
                <Label htmlFor="optionC.ar" className="form-label">
                  {t("answerC")}
                </Label>
                <div className="mb-3">
                  <AppRichEditor
                    initialValue={question?.optionC?.ar || ""}
                    onChange={(data: any) => {
                      form.setFieldValue("optionC.ar", data);
                    }}
                    onFocus={() => {
                      form.setFieldTouched("optionC.ar", true, true);
                    }}
                  />
                  {form.touched.optionC?.ar && form.errors.optionC?.ar ? (
                    <Label className="invalid-feedback d-block">
                      {form.errors.optionC?.ar as string}
                    </Label>
                  ) : null}
                </div>
              </Col>
              <Col xl="6">
                <div className="mb-3">
                  <Label htmlFor="optionD.ar" className="form-label">
                    {t("answerD")}
                  </Label>
                  <AppRichEditor
                    initialValue={question?.optionD?.ar || ""}
                    onChange={(data: any) => {
                      form.setFieldValue("optionD.ar", data);
                    }}
                    onFocus={() => {
                      form.setFieldTouched("optionD.ar", true, true);
                    }}
                  />
                  {form.touched.optionD?.ar && form.errors.optionD?.ar ? (
                    <Label className="invalid-feedback d-block">
                      {form.errors.optionD?.ar as string}
                    </Label>
                  ) : null}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xl="12">
                <Row>
                  <Col sm={4}>
                    <div className="mb-3">
                      <Label htmlFor="correctOption" className="form-label">
                        {t("correctAnswer")}
                      </Label>
                      <Input
                        type="select"
                        id="correctOption"
                        name="correctOption"
                        className="form-select"
                        aria-label={t("chooseCorrectAnswer")}
                        placeholder={t("chooseCorrectAnswer")}
                        value={form.values.correctOption}
                        onChange={form.handleChange}
                        onBlur={form.handleBlur}
                      >
                        <option value={""}>{t("chooseCorrectAnswer")} </option>
                        <option
                          value={QuestionOptionEnum.OPTION_A}
                          key={"A"}
                          className="text-capitalize"
                        >
                          {t("A")}
                        </option>
                        <option
                          value={QuestionOptionEnum.OPTION_B}
                          key={"B"}
                          className="text-capitalize"
                        >
                          {t("B")}
                        </option>
                        <option
                          value={QuestionOptionEnum.OPTION_C}
                          key={"C"}
                          className="text-capitalize"
                        >
                          {t("C")}
                        </option>
                        <option
                          value={QuestionOptionEnum.OPTION_D}
                          key={"D"}
                          className="text-capitalize"
                        >
                          {t("D")}
                        </option>
                      </Input>
                      {form.touched.correctOption &&
                      form.errors.correctOption ? (
                        <FormFeedback type="invalid">
                          {form.errors.correctOption as string}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col sm={4}>
                    <Label htmlFor="marks" className="form-label">
                      {t("marks")}
                    </Label>
                    <div className="mb-3">
                      <div className="input-step w-100">
                        <button
                          type="button"
                          className="minus"
                          onClick={countDownMarks}
                        >
                          –
                        </button>
                        <Input
                          type="text"
                          className="product-quantity w-100"
                          value={form.values.marks}
                          name="marks"
                          onChange={form.handleChange}
                          onBlur={form.handleBlur}
                          readOnly
                        />
                        <button
                          type="button"
                          className="plus"
                          onClick={countUpMarks}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </Col>
                  <Col sm={4}>
                    <Label htmlFor="time" className="form-label">
                      {t("time")}
                    </Label>
                    <div className="mb-3">
                      <TimeInput
                        id="time"
                        name="time"
                        value={form.values.timeDuration}
                        className="form-control"
                        onChangeValue={(value: string) => {
                          form.setFieldValue("timeDuration", value);
                        }}
                      />
                      {form.touched.timeDuration && form.errors.timeDuration ? (
                        <FormFeedback type="invalid">
                          {form.errors.timeDuration as string}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={addingQuestion || updatingQuestion}
              disabled={
                gettingQuestion ||
                addingQuestion ||
                updatingQuestion ||
                getQuestionError ||
                uploadingDisplayImage ||
                gettingAISKillsSuggestion
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
